import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { MediaImageProps } from '@elfsight/widget-media';
import type { LoadImageResult } from '@elfsight/widget-utils';
import { truncateText } from '@elfsight/widget-utils';
import { useLayout } from '../../layout';
import type { BlockProps } from '../../lib/card';
import { CardLayout } from '../../lib/card';
import { themeColors } from '../../themes';
import { useSocialFeedSettings } from '../../utils';
import { Media } from '../components';
import { usePost } from '../Post';
import { getFinalRatio, useMedia, usePostSettings } from '../utils';
import { CardMediaTypeLayer } from './CardMediaTypeLayer';

type MediaStateType = Omit<LoadImageResult, 'src'>;

type OnLoadHandlerType = NonNullable<MediaImageProps['onLoad']>;

interface CardMediaBlockProps extends BlockProps {
  mode?: 'contain' | 'cover' | 'scale-down' | 'none';
  borderRadius?: number;
  popupMediaBackgroundColor?: string;
  ratio?: number;
  hideMediaType?: boolean;
  thumbnail?: boolean;
}

export function CardMediaBlock({
  mode,
  borderRadius,
  popupMediaBackgroundColor,
  ratio = 1,
  hideMediaType = false,
  thumbnail = false,
  ...forwardingProps
}: CardMediaBlockProps) {
  const { layout } = useSocialFeedSettings();
  const {
    isInPopup,
    text = '',
    rect: { width }
  } = usePost();
  const {
    heightNormalization,
    textColor,
    postImageRatio: settingsRatio
  } = usePostSettings();
  const { rerender } = useLayout();

  const [mediaState, setMediaState] = useState<MediaStateType>({
    status: 'pending',
    width: undefined,
    height: undefined
  });
  const { status } = mediaState;

  const media = useMedia();

  const finalRatio = getFinalRatio({ layout, settingsRatio, ratio, isInPopup });

  // @ts-expect-error Проблемы с конфликтом типа ReactEventHandler<HTMLDivElement>
  const handleLoad: OnLoadHandlerType = (_event, size) => {
    setMediaState((prev) => ({ ...prev, status: 'loaded', ...size }));

    if (!rerender) return;

    requestAnimationFrame(() => {
      rerender();
    });
  };

  const handleError = () =>
    setMediaState((prev) => ({ ...prev, status: 'failed' }));

  const maxHeight =
    status === 'pending' && finalRatio ? width * finalRatio : undefined;

  const backgroundImage =
    heightNormalization && status === 'loaded'
      ? media.thumbnail || media.src
      : undefined;

  // Скрываем медиа в одноколоночном отображении, если ширина поста ещё не известна,
  // чтобы исключить прыжки скелетона при загрузке
  const hideInOneColumnCase =
    !width && (layout === 'list' || layout === 'slider');

  if (status === 'failed' || hideInOneColumnCase) {
    return null;
  }

  return (
    <MediaBlock
      {...forwardingProps}
      _maxHeight={maxHeight}
      _backgroundImage={backgroundImage}
      _column
      _center
    >
      <Media
        alt={truncateText(text)}
        ratio={finalRatio}
        mode={mode || (thumbnail ? 'cover' : 'contain')}
        media={media}
        borderRadius={borderRadius}
        backgroundColor={popupMediaBackgroundColor}
        thumbnail={thumbnail}
        onError={handleError}
        onLoad={handleLoad}
        loaderColor={textColor}
      />

      {!hideMediaType && thumbnail && (media?.src || media?.thumbnail) && (
        <CardMediaTypeLayer />
      )}
    </MediaBlock>
  );
}

const MediaBlock = styled(CardLayout.OuterBlock)<{
  _maxHeight?: number;
  _backgroundImage?: string;
}>`
  overflow: hidden;
  ${({ _maxHeight }) => _maxHeight && `max-height: ${_maxHeight}px`};

  ${({ _backgroundImage }) =>
    _backgroundImage &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: url(${_backgroundImage}) ${themeColors.blackColor07};
        background-size: cover;
        background-position: center;
        filter: blur(10px) brightness(0.5);
        transform: scale(1.2);
        z-index: 0;
      }
    `};
`;
